import { useLicensingServiceV1 } from "@rw1/api-services";
import { Licensing_V1 } from "@rw1/auto-generated-clients";
import { useQueries, UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { STALE_TIME_MS } from "./constants";
import { fetchAllPages, useFetchAll } from "./useFetchAll";

export const EXTERNAL_EXPERIENCES_QUERY_KEY = "external-experiences";
export const LICENSE_ALLOCATIONS_QUERY_KEY = "license-allocations";
export const LICENSE_GROUP_ALLOCATIONS_QUERY_KEY = "license-group-allocations";

export interface LicenseWithAllocations {
  license: Licensing_V1.License;
  allocations: Licensing_V1.LicenseUserAllocation[];
  groupAllocations: Licensing_V1.LicenseGroupAllocation[];
}

export interface AllocationWithLicense {
  allocation: Licensing_V1.LicenseUserAllocation;
  license: Licensing_V1.License;
}

export interface GroupAllocationWithLicense {
  allocation: Licensing_V1.LicenseGroupAllocation;
  license: Licensing_V1.License;
}

export interface UseExternalExperiencesResult {
  licenses: LicenseWithAllocations[];
  userIdToAllocationMap: Map<string, AllocationWithLicense[]>;
  groupIdToAllocationMap: Map<string, GroupAllocationWithLicense[]>;
  releaseChannelIdToLicenseMap: Map<string, Licensing_V1.License>;
  allocationQueries: UseQueryResult<Licensing_V1.LicenseUserAllocation[]>[];
  isLoading: boolean;
  error: Error | null;
}

export const useExternalExperiences = (): UseExternalExperiencesResult => {
  const licensingService = useLicensingServiceV1();
  const { companyId } = useParams();
  // Fetch All Licenses
  const {
    data: allLicenses = [],
    isLoading: isLicensesLoading,
    error: licensesError,
  } = useFetchAll<Licensing_V1.License>(
    [EXTERNAL_EXPERIENCES_QUERY_KEY, companyId],
    (page, size) => licensingService.getAllByOrganizationId(companyId!, page, size),
    {
      enabled: !!companyId,
      staleTime: STALE_TIME_MS,
      pageSize: 100,
    },
  );

  const licenses = useMemo(() => {
    return allLicenses.filter(
      (license) => license.assignedOrganizationId === companyId,
    );
  }, [allLicenses, companyId]);

  // Fetch User Allocations
  const allocationQueries = useQueries({
    queries: licenses.map((license) => ({
      queryKey: [LICENSE_ALLOCATIONS_QUERY_KEY, license.licenseId],
      queryFn: () =>
        fetchAllPages(
          (page, size) =>
            licensingService.getLicenseUserAllocations(
              license.licenseId,
              page,
              size,
            ),
          1000,
        ),
      enabled: !!license.licenseId,
      staleTime: STALE_TIME_MS,
      select: (allocations: Licensing_V1.LicenseUserAllocation[]) =>
        allocations.filter((allocation) => !allocation.isRevoked),
    })),
  });

  // Fetch Group Allocations
  const groupAllocationQueries = useQueries({
    queries: licenses.map((license) => ({
      queryKey: [LICENSE_GROUP_ALLOCATIONS_QUERY_KEY, license.licenseId],
      queryFn: () =>
        fetchAllPages(
          (page, size) =>
            licensingService.getLicenseGroupAllocations(
              license.licenseId,
              page,
              size,
            ),
          1000,
        ),
      enabled: !!license.licenseId,
      staleTime: STALE_TIME_MS,
      select: (groupAllocations: Licensing_V1.LicenseGroupAllocation[]) =>
        groupAllocations.filter((allocation) => !allocation.isRevoked),
    })),
  });

  // --- Aggregation Logic ---

  const isAllocationsLoading =
    allocationQueries.some((query) => query.isLoading) ||
    groupAllocationQueries.some((query) => query.isLoading);

  const allocationError =
    allocationQueries.find((query) => query.error)?.error ??
    groupAllocationQueries.find((query) => query.error)?.error;

  const userIdToAllocationMap = useMemo(() => {
    const map = new Map<string, AllocationWithLicense[]>();
    for (let i = 0; i < allocationQueries.length; i++) {
      const query = allocationQueries[i];
      const license = licenses[i];
      if (!query || !license) continue;

      const allocations = query.data ?? [];
      for (const allocation of allocations) {
        const existing = map.get(allocation.userId) ?? [];
        existing.push({ allocation, license });
        map.set(allocation.userId, existing);
      }
    }

    return map;
  }, [allocationQueries, licenses]);

  const groupIdToAllocationMap = useMemo(() => {
    const map = new Map<string, GroupAllocationWithLicense[]>();
    for (let i = 0; i < groupAllocationQueries.length; i++) {
      const query = groupAllocationQueries[i];
      const license = licenses[i];
      if (!query || !license) continue;

      const groupAllocations = query.data ?? [];
      for (const allocation of groupAllocations) {
        const existing = map.get(allocation.userGroupId) ?? [];
        existing.push({ allocation, license });
        map.set(allocation.userGroupId, existing);
      }
    }

    return map;
  }, [groupAllocationQueries, licenses]);

  const licensesWithAllocations: LicenseWithAllocations[] = useMemo(() => {
    return licenses.map((license, index) => ({
      license,
      allocations: allocationQueries[index]?.data ?? [],
      groupAllocations: groupAllocationQueries[index]?.data ?? [],
    }));
  }, [licenses, allocationQueries, groupAllocationQueries]);

  const releaseChannelIdToLicenseMap = useMemo(() => {
    const map = new Map<string, Licensing_V1.License>();
    for (const license of licenses) {
      map.set(license.releaseChannelId, license);
    }

    return map;
  }, [licenses]);

  return {
    licenses: licensesWithAllocations,
    userIdToAllocationMap,
    groupIdToAllocationMap,
    releaseChannelIdToLicenseMap,
    allocationQueries,
    isLoading: isLicensesLoading || isAllocationsLoading,
    error: licensesError ?? allocationError ?? null,
  };
};
